import type { jsPDF } from 'jspdf';
import regular_ from "../../assets/font/JetBrainsMono-Regular.ttf";
import bold_ from "../../assets/font/JetBrainsMono-Bold.ttf";
/**
 * Registers JetBrains Mono (regular + bold) on a jsPDF document instance.
 * jsPDF's addFont/addFileToVFS are per-document, not global — this must
 * run once on every new jsPDF() instance before drawing anything that
 * references the 'JetBrainsMono' font name (see theme.ts `fonts.mono`).
 */
export function registerJetBrainsMono(
  doc: jsPDF,
  regularBase64: string,
  boldBase64: string,
): void {
  doc.addFileToVFS('JetBrainsMono-Regular.ttf', regularBase64);
  doc.addFont('JetBrainsMono-Regular.ttf', 'JetBrainsMono', 'normal');

  doc.addFileToVFS('JetBrainsMono-Bold.ttf', boldBase64);
  doc.addFont('JetBrainsMono-Bold.ttf', 'JetBrainsMono', 'bold');
}

/**
 * Browser helper — fetches the two .ttf files (place them in your app's
 * public/fonts/ directory) and returns base64 payloads ready for
 * registerJetBrainsMono. The Node smoke test doesn't use this; it reads
 * the same files straight off disk instead, since there's no fetch/public
 * dir in that context.
 */
export async function loadJetBrainsMonoBase64(
  regularUrl = regular_,
  boldUrl = bold_,
): Promise<{ regular: string; bold: string }> {
  const toBase64 = async (url: string): Promise<string> => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to load font at ${url}: HTTP ${res.status}`);
    const buffer = await res.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  };

  const [regular, bold] = await Promise.all([toBase64(regularUrl), toBase64(boldUrl)]);
  return { regular, bold };
}